import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Payment,
  Visibility,
  VisibilityOff,
  Save,
  Science,
  CheckCircle,
  ErrorOutline,
  Link as LinkIcon,
} from '@mui/icons-material';
import { loadRazorpayCheckout } from '../utils/loadRazorpayScript';

const RazorpaySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [hasKeySecret, setHasKeySecret] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.get('/admin/settings/razorpay');
      if (data.success) {
        setKeyId(data.keyId || '');
        setHasKeySecret(!!data.hasKeySecret);
        setConfigured(!!data.configured);
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to load Razorpay settings',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadRazorpayCheckout()
      .then(() => setScriptReady(true))
      .catch(() => setScriptReady(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      if (!hasKeySecret && !keySecret.trim()) {
        setMessage({ type: 'error', text: 'Key Secret is required on first setup.' });
        setSaving(false);
        return;
      }

      const payload = { keyId: keyId.trim() };
      if (keySecret.trim()) payload.keySecret = keySecret.trim();

      const { data } = await axios.post('/admin/settings/razorpay', payload);
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        setKeySecret('');
        setConfigured(!!data.configured);
        setHasKeySecret(!!data.configured || hasKeySecret);
        await loadSettings();
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to save Razorpay settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestCheckout = async () => {
    setTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const Razorpay = await loadRazorpayCheckout();
      setScriptReady(true);

      const { data } = await axios.post('/admin/settings/razorpay/test-order');
      if (!data.success) {
        throw new Error(data.message || 'Could not create test order');
      }

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: 'DrainGo Admin',
        description: 'Razorpay connection test (₹1)',
        order_id: data.order_id,
        theme: { color: '#116611' },
        handler(response) {
          setMessage({
            type: 'success',
            text: `Test payment successful. Payment ID: ${response.razorpay_payment_id}`,
          });
        },
        modal: {
          ondismiss: () => {
            setMessage({ type: 'info', text: 'Test checkout closed.' });
          },
        },
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setMessage({
          type: 'error',
          text: resp.error?.description || 'Test payment failed',
        });
      });
      rzp.open();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || err.message || 'Test checkout failed',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#116611' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <Payment sx={{ color: '#116611', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Razorpay Payment Gateway
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure API keys used by the driver app for membership payments. Keys are stored securely on the server.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <Chip
            icon={configured ? <CheckCircle /> : <ErrorOutline />}
            label={configured ? 'Keys configured' : 'Not configured'}
            color={configured ? 'success' : 'warning'}
            size="small"
          />
          <Chip
            icon={scriptReady ? <CheckCircle /> : <ErrorOutline />}
            label={scriptReady ? 'Checkout script loaded' : 'Script loading…'}
            color={scriptReady ? 'success' : 'default'}
            size="small"
          />
          {hasKeySecret && (
            <Chip label="Secret saved on server" color="info" size="small" variant="outlined" />
          )}
        </Stack>

        {message.text && (
          <Alert severity={message.type === 'info' ? 'info' : message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSave}>
          <TextField
            fullWidth
            required
            label="Razorpay Key ID"
            placeholder="rzp_test_xxxxxxxx or rzp_live_xxxxxxxx"
            value={keyId}
            onChange={(e) => setKeyId(e.target.value)}
            margin="normal"
            helperText="From Razorpay Dashboard → Account & Settings → API Keys"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            type={showSecret ? 'text' : 'password'}
            label="Razorpay Key Secret"
            placeholder={hasKeySecret ? 'Leave blank to keep existing secret' : 'Enter key secret'}
            value={keySecret}
            onChange={(e) => setKeySecret(e.target.value)}
            margin="normal"
            helperText={
              hasKeySecret
                ? 'Secret is already stored. Enter a new value only to replace it.'
                : 'Required for the first save'
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowSecret(!showSecret)} edge="end" size="small">
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Divider sx={{ my: 3 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !keyId.trim()}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
            >
              {saving ? 'Saving…' : 'Save API Keys'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={testing || !configured || !scriptReady}
              onClick={handleTestCheckout}
              startIcon={testing ? <CircularProgress size={18} /> : <Science />}
            >
              {testing ? 'Opening checkout…' : 'Test payment (₹1)'}
            </Button>
          </Stack>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" component="div">
            <strong>Where to get keys:</strong>{' '}
            <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer">
              Razorpay Dashboard → API Keys
            </a>
            . Use <strong>Test Mode</strong> keys while developing.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Checkout script: checkout.razorpay.com/v1/checkout.js (loaded in admin only for testing).
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

export default RazorpaySettings;
